<template>
  <v-card outlined class="mb-3">
    <v-card-text>
      <div
        class="subtitle-1 black--text font-weight-medium d-flex align-center"
      >
        <v-img :src="platform.logo_url" max-width="25px" class="mr-2"></v-img>
        Google Business Profile Settings
      </div>
      <div class="px-3 py-2">
        <v-row>
          <v-col cols="12" md="6" class="pb-2" v-if="isInSocialMedia">
            <autocomplete
              label="Choose locations"
              chips
              deletable-chips
              small-chips
              @input="$emit('input', form)"
              v-model="form.gmb_locations"
              :items="locations"
              multiple
            ></autocomplete>
          </v-col>
          <v-col cols="12" md="4" class="py-2">
            <select-field
              @input="$emit('input', form)"
              label="Post Type"
              :items="postTypes"
              v-model="form.gmb_post_type"
            />
          </v-col>
          <template v-if="form.gmb_post_type !== 'OFFER'">
            <v-col cols="12" md="4" class="py-2">
              <select-field
                @input="$emit('input', form)"
                label="Call to action"
                :items="callToAction"
                v-model="form.gmb_callToAction"
              />
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                v-if="form.gmb_callToAction && form.gmb_callToAction != 'CALL'"
                label="Call to action URL"
                outlined
                @input="$emit('input', form)"
                v-model="form.gmb_callToActionUrl"
                :rules="rules"
              ></v-text-field>
            </v-col>
          </template>
          <template v-else>
            <!-- Required Fields for OFFER -->
            <v-col cols="12" md="12" class="pb-0">
              <v-text-field
                label="Offer title*"
                outlined
                dense
                @input="$emit('input', form)"
                v-model="form.gmb_offerDetails.title"
                :rules="[rules.required, rules.maxLength50]"
                :counter="58"
                hint="Example: 20% off in store or online"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="displayStartDate"
                    label="Start date*"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    :rules="[rules.required]"
                    hint="DD/MM/YYYY"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.gmb_offerDetails.start"
                  @input="handleStartDateChange"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="displayEndDate"
                    label="End Date*"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    :rules="[rules.required]"
                    hint="DD/MM/YYYY"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.gmb_offerDetails.end"
                  @input="handleEndDateChange"
                  :min="endDateMin"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <!-- Optional Fields Section -->
            <v-col cols="12">
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Add more details (optional)
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="6" class="pb-0">
                        <v-text-field
                          label="Voucher code"
                          outlined
                          dense
                          height="40"
                          class="input-field-fixed-height"
                          @input="$emit('input', form)"
                          v-model="form.gmb_offerDetails.couponCode"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6" class="pb-0">
                        <v-text-field
                          label="Link to redeem offer"
                          outlined
                          dense
                          height="40"
                          class="input-field-fixed-height"
                          @input="$emit('input', form)"
                          v-model="form.gmb_offerDetails.redemptionUrl"
                          :rules="[rules.validUrl]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" class="pb-0">
                        <v-textarea
                          label="Terms and conditions"
                          outlined
                          @input="$emit('input', form)"
                          v-model="form.gmb_offerDetails.terms"
                          :counter="4800"
                          :rules="[rules.maxLength4800]"
                          rows="3"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </template>
        </v-row>
      </div>
      <div>
        <p
          v-if="form.gmb_callToAction && form.gmb_callToAction == 'CALL'"
          class="text-warning font-medium text-orange-300"
        >
          ! Ensure your Google Business Profile has a phone number.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import SelectField from "../../ui/form/SelectField.vue";
import Autocomplete from "@/components/ui/form/Autocomplete.vue";
import rulesConstants from "@/assets/constants/rules.constants";
import moment from "moment";

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    configuration: {
      type: Object,
    },
    isEditing: Boolean,
    isRepost: Boolean,
    isDraftPost: Boolean,
    locations: Array,
    connectedApps: {
      type: Array,
      default: () => [],
    },
    post: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    SelectField,
    Autocomplete,
  },
  data() {
    return {
      rules: {
        required: (v) => !!v || "This field is required",
        validUrl: (v) =>
          !v ||
          /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(
            v
          ) ||
          "Please enter a valid URL",
        maxLength50: (v) =>
          !v || v.length <= 58 || "Maximum 58 characters allowed",
        maxLength4800: (v) =>
          !v || v.length <= 4800 || "Maximum 4800 characters allowed",
        endDateValidation: (v) => {
          if (!v) return "End date is required";

          const today = moment().startOf("day");
          const tomorrow = moment().add(1, "day").startOf("day");
          const end = moment(v).startOf("day");

          // If no start date selected yet, end date must be at least tomorrow
          if (!this.form.gmb_offerDetails.start) {
            return (
              end.isSameOrAfter(tomorrow) ||
              "End date must be at least tomorrow"
            );
          }

          const start = moment(this.form.gmb_offerDetails.start).startOf("day");

          // If start date is past/current, end date must be at least tomorrow
          if (start.isSameOrBefore(today)) {
            return (
              end.isSameOrAfter(tomorrow) ||
              "End date must be at least tomorrow"
            );
          }

          // If start date is future, end date must be at least one day after start
          return (
            end.isAfter(start) ||
            "End date must be at least one day after the start date"
          );
        },
      },
      startDateMenu: false,
      endDateMenu: false,
      form: {
        gmb_callToAction: "",
        gmb_locations: [],
        gmb_callToActionUrl: "",
        gmb_post_type: "STANDARD",
        gmb_offerDetails: {
          title: "",
          couponCode: "",
          redemptionUrl: "",
          terms: "",
          start: "",
          end: "",
        },
      },
      postTypes: [
        {
          name: "Standard Post",
          value: "STANDARD",
        },
        {
          name: "Offer Post",
          value: "OFFER",
        },
      ],
      callToAction: [
        {
          name: "Book",
          value: "BOOK",
        },
        {
          name: "Order",
          value: "ORDER",
        },
        {
          name: "Shop",
          value: "SHOP",
        },
        {
          name: "Learn More",
          value: "LEARN_MORE",
        },
        {
          name: "Sign Up",
          value: "SIGN_UP",
        },
        {
          name: "Call",
          value: "CALL",
        },
      ],
    };
  },
  async created() {
    if (this.isEditing || this.isRepost || this.isDraftPost) {
      this.$watch(
        () => this.connectedApps,
        (newVal) => {
          if (newVal && newVal.length > 0) {
            this.updateConfiguration();
          }
        }
      );
    }
  },
  watch: {
    isDraftPost: {
      handler(newVal) {
        if (newVal) {
          this.updateConfiguration();
        }
      },
      immediate: true,
    },
    isEditing: {
      handler(newVal) {
        if (newVal) {
          // If isEditing is true, update the form with configuration values
          if (this.isEditing) {
            this.updateConfiguration();
          }
        }
      },
      immediate: true,
    },
    isRepost: {
      handler(newVal) {
        if (newVal) {
          // If isEditing is true, update the form with configuration values
          if (this.isRepost) {
            this.updateConfiguration();
          }
        }
      },
      immediate: true,
    },
    value: {
      handler(val) {
        // console.log(val);
        Object.keys(this.form).forEach((key) => {
          if (val[key] != undefined) {
            this.form[key] = val[key];
          }
        });
      },
      deep: true,
    },
    "form.gmb_post_type": {
      handler(newValue) {
        if (newValue === "OFFER") {
          this.form.gmb_callToAction = "CALL";
        }
      },
      immediate: true,
    },
  },
  computed: {
    endDateMin() {
      const today = moment().startOf("day");
      const start = this.form.gmb_offerDetails.start
        ? moment(this.form.gmb_offerDetails.start).startOf("day")
        : null;

      // If start date is not set or is past/current, minimum end date is tomorrow
      if (!start || start.isSameOrBefore(today)) {
        return moment().add(1, "day").format("YYYY-MM-DD");
      }

      // If start date is future, minimum end date is start date + 1
      return moment(start).add(1, "day").format("YYYY-MM-DD");
    },
    isInSocialMedia() {
      return (this.post.social_media || []).includes("google_business_profile");
    },
    platform() {
      // console.log("loc:", this.locations);
      let platforms = this.$store.getters.flyPlatforms;
      let platform = platforms.find(
        (i) => i.alias == "google_business_profile"
      );
      return platform || {};
    },
    displayStartDate: {
      get() {
        return this.form.gmb_offerDetails.start
          ? moment(this.form.gmb_offerDetails.start).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.form.gmb_offerDetails.start = value
          ? moment(value).format("YYYY-MM-DD")
          : "";
        this.$emit("input", this.form);
      },
    },
    displayEndDate: {
      get() {
        return this.form.gmb_offerDetails.end
          ? moment(this.form.gmb_offerDetails.end).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.form.gmb_offerDetails.end = value
          ? moment(value).format("YYYY-MM-DD")
          : "";
        this.$emit("input", this.form);
      },
    },
  },
  methods: {
    initForm() {
      console.log("loc:", this.locations);

      Object.keys(this.form).forEach((key) => {
        if (this.value[key] != undefined) {
          this.form[key] = this.value[key];
        }
      });
    },
    updateConfiguration() {
      const isGMBConnected = this.connectedApps.find(
        (app) => app.id === "google_business_profile"
      );
      if (isGMBConnected) {
        const item =
          this.connectedApps.find(
            (item) => item.id === "google_business_profile"
          ) || [];

        let accounts = item.accounts;
        if (accounts && accounts.length > 0) {
          let allLocations = accounts.flatMap((account) => account.locations);

          // Filter the locations that match configuration locations
          let locations = allLocations.filter((location) =>
            this.configuration.gmb_locations?.some(
              (configLoc) => configLoc.name === location.name
            )
          );

          // Store the found locations and other data in the form
          this.form.gmb_locations =
            locations.length > 0
              ? locations
              : this.configuration.gmb_locations || "";
        } else {
          // If no accounts or locations are found, set the default configuration values
          this.form.gmb_locations = null;
        }
      } else {
        // If GMB is not connected, set the default configuration values
        this.form.gmb_locations = null;
      }
      this.form.gmb_post_type = this.configuration.gmb_post_type || "STANDARD";
      this.form.gmb_callToAction = this.configuration.gmb_callToAction || "";
      this.form.gmb_callToActionUrl =
        this.configuration.gmb_callToActionUrl || "";
      if (this.configuration.gmb_offerDetails) {
        this.form.gmb_offerDetails = {
          ...this.form.gmb_offerDetails,
          ...this.configuration.gmb_offerDetails,
          title: this.configuration.gmb_offerDetails.title || "",
        };
      }
    },
    handleStartDateChange(date) {
      this.startDateMenu = false;

      // normalize YYYY-MM-DD -> keep internal format consistent
      const start = moment(date).format("YYYY-MM-DD");

      // Set start date
      this.form.gmb_offerDetails.start = start;

      // Adjust end date if needed
      const startMoment = moment(start).startOf("day");
      const today = moment().startOf("day");
      const currentEnd = moment(this.form.gmb_offerDetails.end).startOf("day");

      // If start is past/current, end must be at least tomorrow
      if (startMoment.isSameOrBefore(today)) {
        const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
        if (
          !currentEnd.isValid() ||
          !currentEnd.isSameOrAfter(moment(tomorrow))
        ) {
          this.form.gmb_offerDetails.end = tomorrow;
        }
      } else {
        // If start is future, end must be at least start + 1
        const minEnd = startMoment.add(1, "day").format("YYYY-MM-DD");
        if (
          !currentEnd.isValid() ||
          !currentEnd.isSameOrAfter(moment(minEnd))
        ) {
          this.form.gmb_offerDetails.end = minEnd;
        }
      }

      this.$emit("input", this.form);
    },
    handleEndDateChange(date) {
      this.endDateMenu = false;
      this.$emit("input", this.form);
    },
  },
  beforeMount() {
    this.initForm();
  },
};
</script>

<style scoped>
:deep(.input-field-fixed-height .v-input__control) {
  min-height: 40px;
}
:deep(.input-field-fixed-height .v-input__slot) {
  min-height: 40px;
}
</style>
